/**
 * WreckageDialog — Manage destroyed ship wrecks: view status, request towing,
 * and initiate repairs.
 *
 * Side-by-side layout: wreck list (left) | detail panel (right).
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager } from '../../managers/MMOMonetizationManager';
import type { MMOStateManager } from '../../managers/MMOStateManager';
import type { UnitWreck, TowOption } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, createDialogChrome, closeDialog, Fonts, FontSize, Radius } from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';
import { EventBus } from '../../utils/EventBus';

const PAD = 10;

const STATUS_COLORS: Record<string, string> = {
  awaiting_decision: '#ffcc44',
  towing: '#4488ff',
  arrived: '#44cc44',
  expired: '#888888',
  declined: '#ff4444',
};

const REPAIR_STATUS_COLORS: Record<string, string> = {
  not_started: '#888888',
  repairing: '#4488ff',
  completed: '#44cc44',
};

/** Format remaining time as days / hours / minutes */
function formatTimeRemaining(expiresAt: string): { text: string; color: string } {
  const now = Date.now();
  const remainMs = new Date(expiresAt).getTime() - now;
  if (remainMs <= 0) return { text: I18n.t('time.expired'), color: '#ff4444' };

  const totalMin = Math.floor(remainMs / 60000);
  const totalHours = Math.floor(totalMin / 60);
  const totalDays = Math.floor(totalHours / 24);

  if (totalDays >= 1) {
    const hrs = totalHours - totalDays * 24;
    return { text: `${totalDays}${I18n.t('time.days')} ${hrs}${I18n.t('time.hours')}`, color: '#889999' };
  }
  if (totalHours >= 1) {
    const mins = totalMin - totalHours * 60;
    return { text: `${totalHours}${I18n.t('time.hours')} ${mins}${I18n.t('time.minutes')}`, color: totalHours < 6 ? '#ffcc44' : '#889999' };
  }
  return { text: `${totalMin}${I18n.t('time.minutes')}`, color: '#ff4444' };
}

export class WreckageDialog extends Phaser.GameObjects.Container {
  private monetization: MMOMonetizationManager;
  private mmoState: MMOStateManager;
  private wrecks: UnitWreck[] = [];
  private selectedWreck: UnitWreck | null = null;
  private towOptions: TowOption[] = [];

  private listContainer: Phaser.GameObjects.Container;
  private detailContainer: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text;
  private repairPollTimer: Phaser.Time.TimerEvent | null = null;
  private repairCompletionShown = false;

  // Responsive
  private mobile: boolean;
  private listW: number;
  private detailW: number;
  private dh: number;
  // Detail scroll state
  private detailScrollY = 0;
  private detailMaxScrollY = 0;
  private isDraggingDetailScroll = false;
  private detailDragStartY = 0;
  private detailDragStartScrollY = 0;

  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const _dw = mobile ? 340 : 652;
    const _dh = mobile ? 540 : 600;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - _dw / 2, height / 2 - _dh / 2);
    this.mobile = mobile;
    this.dh = _dh;
    const dw = _dw;
    const dh = _dh;

    // Responsive layout
    const LIST_W = mobile ? dw - PAD * 2 : 260;
    const DETAIL_X = LIST_W + PAD * 2 + 10;
    this.listW = LIST_W;
    this.detailW = mobile ? dw - PAD * 2 : dw - DETAIL_X - PAD;

    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);
    this.mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.wreckage'),
      onClose: () => this.close(),
    });

    // Left: wreck list
    this.listContainer = scene.add.container(PAD, 58);
    this.add(this.listContainer);

    // Detail panel: overlay on mobile, side-by-side on desktop
    this.detailContainer = scene.add.container(mobile ? PAD : DETAIL_X, 58);
    if (mobile) this.detailContainer.setVisible(false);
    this.add(this.detailContainer);

    // Scroll mask for detail panel
    const detailVisibleH = dh - 58 - 30;
    const detailMaskShape = scene.make.graphics({});
    const detailMaskX = this.x + (mobile ? PAD : DETAIL_X);
    const detailMaskY = this.y + 58;
    detailMaskShape.fillRect(detailMaskX, detailMaskY, this.detailW, detailVisibleH);
    this.detailContainer.setMask(new Phaser.Display.Masks.GeometryMask(scene, detailMaskShape));

    // Scroll handlers for detail panel (wheel + touch drag)
    scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: any, _dx: number, dy: number, _dz: number) => {
      if (!this.visible || !this.detailContainer.visible) return;
      this.detailScrollY = Phaser.Math.Clamp(this.detailScrollY + dy * 0.3, 0, Math.max(0, this.detailMaxScrollY));
      this.detailContainer.y = 58 - this.detailScrollY;
    });
    scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.visible || !this.detailContainer.visible) return;
      const localX = pointer.x - this.x;
      const localY = pointer.y - this.y;
      const detailLeft = mobile ? PAD : DETAIL_X;
      if (localX >= detailLeft && localX <= detailLeft + this.detailW && localY >= 58 && localY <= 58 + detailVisibleH) {
        this.isDraggingDetailScroll = true;
        this.detailDragStartY = pointer.y;
        this.detailDragStartScrollY = this.detailScrollY;
      }
    });
    scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.isDraggingDetailScroll || !this.visible) return;
      const dy = this.detailDragStartY - pointer.y;
      this.detailScrollY = Phaser.Math.Clamp(this.detailDragStartScrollY + dy, 0, Math.max(0, this.detailMaxScrollY));
      this.detailContainer.y = 58 - this.detailScrollY;
    });
    scene.input.on('pointerup', () => { this.isDraggingDetailScroll = false; });

    // Status text at bottom
    this.statusText = scene.add.text(dw / 2, dh - 15, '', {
      fontSize: FontSize.sm,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadData();
  }

  private close(): void {
    this.stopRepairPolling();
    closeDialog(this.scene, this);
  }

  private startRepairPolling(): void {
    if (this.repairPollTimer) return; // already polling
    this.repairPollTimer = this.scene.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        // Re-render detail to update timer bar (uses live Date.now() vs repairStartedAt)
        if (this.selectedWreck?.repairStatus === 'repairing') {
          this.renderDetail();
        }
        // Also refresh from server every 5 seconds to catch completion
        if (this.repairPollTimer && this.repairPollTimer.getElapsed() % 5000 < 1100) {
          this.loadData();
        }
      },
    });
  }

  private stopRepairPolling(): void {
    if (this.repairPollTimer) {
      this.repairPollTimer.remove();
      this.repairPollTimer = null;
    }
  }

  private showRepairCompletePopup(wreck: UnitWreck): void {
    this.stopRepairPolling();
    const { width, height } = this.scene.cameras.main;
    const pw = 320;
    const ph = 160;
    const popup = this.scene.add.container(width / 2 - pw / 2, height / 2 - ph / 2);
    popup.setDepth(3000);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x0a1428, 0.97);
    bg.fillRoundedRect(0, 0, pw, ph, 8);
    bg.lineStyle(2, 0x44cc44, 0.6);
    bg.strokeRoundedRect(0, 0, pw, ph, 8);
    popup.add(bg);

    const variantName = wreck.shipVariant || wreck.unitType;
    const title = this.scene.add.text(pw / 2, 24, 'REPAIR COMPLETE', {
      fontSize: '16px', fontStyle: '900', color: '#44cc44',
      fontFamily: Fonts.heading, letterSpacing: 2,
    });
    title.setOrigin(0.5, 0);
    popup.add(title);

    const msg = this.scene.add.text(pw / 2, 58, `${variantName} has been fully restored\nand is ready for duty.`, {
      fontSize: '13px', color: '#c0d0e0', fontFamily: Fonts.body,
      align: 'center',
    });
    msg.setOrigin(0.5, 0);
    popup.add(msg);

    // Dismiss button
    const btnW = 120;
    const btnH = 32;
    const btnX = (pw - btnW) / 2;
    const btnY = ph - 48;
    const btnBg = this.scene.add.graphics();
    btnBg.fillStyle(0x226622, 1);
    btnBg.fillRoundedRect(btnX, btnY, btnW, btnH, 4);
    btnBg.lineStyle(1, 0x44cc44, 0.4);
    btnBg.strokeRoundedRect(btnX, btnY, btnW, btnH, 4);
    popup.add(btnBg);

    const btnText = this.scene.add.text(pw / 2, btnY + btnH / 2, 'DISMISS', {
      fontSize: '12px', fontStyle: '900', color: '#44cc44', fontFamily: Fonts.heading,
    });
    btnText.setOrigin(0.5, 0.5);
    popup.add(btnText);

    const zone = this.scene.add.zone(btnX, btnY, btnW, btnH).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, btnH), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerdown', () => {
      popup.destroy();
      this.loadData(); // refresh wreck list

      // Request chunk reload so the repaired ship appears in the game world
      EventBus.emit('send_message', {
        type: 'mmo_request_chunks',
        payload: { chunks: [{ chunkX: 0, chunkY: 0 }] },
      });
      // Close the wreckage dialog so the player sees their new ship
      this.destroy();
    });
    popup.add(zone);

    this.scene.add.existing(popup);
  }

  // ============ Mobile Back Button ============

  private createMobileBackBtn(): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(0, 0);
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1e2844, 0.9);
    bg.fillRoundedRect(0, 0, 80, 26, 4);
    bg.lineStyle(1, 0x00ffff, 0.1);
    bg.strokeRoundedRect(0, 0, 80, 26, 4);
    btn.add(bg);
    btn.add(this.scene.add.text(40, 13, '\u2190 Back', {
      fontSize: '12px', color: '#8899bb', fontFamily: 'Share Tech Mono, monospace',
    }).setOrigin(0.5));
    const zone = this.scene.add.zone(0, 0, 80, 26).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, 80, 26), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerdown', () => {
      this.listContainer.setVisible(true);
      this.detailContainer.setVisible(false);
    });
    btn.add(zone);
    return btn;
  }

  private async loadData(): Promise<void> {
    this.statusText.setText('Loading...');
    this.wrecks = await this.monetization.fetchWrecks();
    this.statusText.setText('');

    // Update selectedWreck with fresh data, or auto-select first wreck
    if (this.selectedWreck) {
      const updated = this.wrecks.find(w => w.id === this.selectedWreck!.id);
      this.selectedWreck = updated || (this.wrecks.length > 0 ? this.wrecks[0] : null);
    } else if (this.wrecks.length > 0) {
      this.selectedWreck = this.wrecks[0];
    }

    this.renderList();

    // Load detail (including tow options) for auto-selected wreck
    if (this.selectedWreck) {
      this.loadWreckDetail();
      // On mobile with only 1 wreck, skip the list and show detail directly
      if (this.mobile && this.wrecks.length === 1) {
        this.listContainer.setVisible(false);
        this.detailContainer.setVisible(true);
      }
    } else {
      this.renderDetail();
    }
  }

  // ============ Wreck List (left column) ============

  private renderList(): void {
    this.listContainer.removeAll(true);
    let y = 0;

    // Section heading
    const headingLabel = this.wrecks.length > 0
      ? `YOUR WRECKS: ${this.wrecks.length}`
      : 'YOUR WRECKS';
    const heading = this.scene.add.text(0, y, headingLabel, {
      fontSize: FontSize.md,
      color: CSSColors.textLight,
      fontFamily: Fonts.heading,
    });
    this.listContainer.add(heading);
    y += 26;

    if (this.wrecks.length === 0) {
      const empty = this.scene.add.text(0, y, 'No wrecks.\nYour ships are safe!', {
        fontSize: FontSize.sm,
        color: '#667788',
        fontFamily: Fonts.body,
      });
      this.listContainer.add(empty);
      return;
    }

    for (const wreck of this.wrecks) {
      const isSelected = this.selectedWreck?.id === wreck.id;
      const card = this.scene.add.container(0, y);
      const cardH = 62;

      // Card background
      const cardBg = this.scene.add.graphics();
      cardBg.fillStyle(isSelected ? 0x253a66 : 0x161630, 0.8);
      cardBg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      cardBg.lineStyle(1, isSelected ? 0xcc4444 : Colors.cyan, isSelected ? 0.4 : 0.08);
      cardBg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
      card.add(cardBg);

      // Ship type (ALL CAPS)
      const typeLabel = (wreck.shipVariant || wreck.unitType).replace(/_/g, ' ').toUpperCase();
      const typeText = this.scene.add.text(10, 8, typeLabel, {
        fontSize: FontSize.sm,
        color: '#ffffff',
        fontFamily: Fonts.heading,
      });
      card.add(typeText);

      // Tow status
      const towColor = STATUS_COLORS[wreck.towStatus] || '#888888';
      const towLabel = wreck.towStatus.replace(/_/g, ' ');
      const towText = this.scene.add.text(10, 26, `Tow: ${towLabel}`, {
        fontSize: FontSize.xs,
        color: towColor,
        fontFamily: Fonts.body,
      });
      card.add(towText);

      // Repair status
      const repairColor = REPAIR_STATUS_COLORS[wreck.repairStatus] || '#888888';
      const repairLabel = wreck.repairStatus.replace(/_/g, ' ');
      const repairText = this.scene.add.text(10, 42, `Repair: ${repairLabel}`, {
        fontSize: FontSize.xs,
        color: repairColor,
        fontFamily: Fonts.body,
      });
      card.add(repairText);

      // Time remaining (right-aligned)
      const timeInfo = formatTimeRemaining(wreck.expiresAt);
      const timeText = this.scene.add.text(this.listW - 10, 8, timeInfo.text, {
        fontSize: FontSize.xs,
        color: timeInfo.color,
        fontFamily: Fonts.body,
      });
      timeText.setOrigin(1, 0);
      card.add(timeText);

      // Position (right-aligned below time)
      const posText = this.scene.add.text(this.listW - 10, 26, `(${wreck.positionQ},${wreck.positionR})`, {
        fontSize: FontSize.xs,
        color: '#889999',
        fontFamily: Fonts.body,
      });
      posText.setOrigin(1, 0);
      card.add(posText);

      // Interactive hit area
      const hitZone = this.scene.add.zone(0, 0, this.listW, cardH).setOrigin(0, 0);
      hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.listW, cardH), Phaser.Geom.Rectangle.Contains);
      hitZone.on('pointerover', () => {
        if (!isSelected) {
          cardBg.clear();
          cardBg.fillStyle(0x1e1e44, 0.9);
          cardBg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
          cardBg.lineStyle(1, Colors.cyan, 0.15);
          cardBg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
        }
        this.scene.input.setDefaultCursor('pointer');
      });
      hitZone.on('pointerout', () => {
        cardBg.clear();
        cardBg.fillStyle(isSelected ? 0x253a66 : 0x161630, 0.8);
        cardBg.fillRoundedRect(0, 0, this.listW, cardH, Radius.sm);
        cardBg.lineStyle(1, isSelected ? 0xcc4444 : Colors.cyan, isSelected ? 0.4 : 0.08);
        cardBg.strokeRoundedRect(0, 0, this.listW, cardH, Radius.sm);
        this.scene.input.setDefaultCursor('default');
      });
      hitZone.on('pointerdown', () => {
        this.selectedWreck = wreck;
        this.towOptions = [];
        this.renderList();
        this.loadWreckDetail();
        if (this.mobile) {
          this.listContainer.setVisible(false);
          this.detailContainer.setVisible(true);
        }
      });
      card.add(hitZone);

      this.listContainer.add(card);
      y += cardH + 6;
    }
  }

  // ============ Detail Panel (right column) ============

  private async loadWreckDetail(): Promise<void> {
    if (!this.selectedWreck) return;

    // Reset detail scroll position
    this.detailScrollY = 0;
    this.detailContainer.y = 58;

    if (this.selectedWreck.towStatus === 'awaiting_decision') {
      this.statusText.setText('Loading tow options...');
      this.towOptions = await this.monetization.fetchTowOptions(this.selectedWreck.id);
      this.statusText.setText('');
    }

    this.renderDetail();
  }

  private renderDetail(): void {
    this.detailContainer.removeAll(true);

    if (!this.selectedWreck) {
      const hint = this.scene.add.text(this.detailW / 2, 60, 'Select a wreck\nto view details', {
        fontSize: FontSize.sm,
        color: '#556677',
        fontFamily: Fonts.body,
        align: 'center',
      });
      hint.setOrigin(0.5, 0);
      this.detailContainer.add(hint);
      return;
    }

    const wreck = this.selectedWreck;
    let y = 0;

    // Mobile back button
    if (this.mobile) {
      const backBtn = this.createMobileBackBtn();
      this.detailContainer.add(backBtn);
      y += 30;
    }

    // Ship type title (ALL CAPS)
    const typeLabel = (wreck.shipVariant || wreck.unitType).replace(/_/g, ' ').toUpperCase();
    const titleText = this.scene.add.text(0, y, typeLabel, {
      fontSize: FontSize.xl,
      color: '#ff8888',
      fontFamily: Fonts.heading,
    });
    this.detailContainer.add(titleText);
    y += 28;

    // Info rows
    this.addInfoRow(0, y, 'Max HP', `${wreck.maxHp}`);
    y += 20;

    this.addInfoRow(0, y, 'Shields', `${wreck.maxShields}`);
    y += 20;

    this.addInfoRow(0, y, 'Position', `(${wreck.positionQ}, ${wreck.positionR})`);
    y += 20;

    // Destroyed at
    const destroyedDate = new Date(wreck.destroyedAt).toLocaleString();
    this.addInfoRow(0, y, 'Destroyed', destroyedDate);
    y += 20;

    // Destroyed by (if available)
    if (wreck.destroyedByName) {
      this.addInfoRow(0, y, 'Destroyed By', wreck.destroyedByName, '#ff8888');
      y += 20;
    } else if (wreck.destroyedByUserId) {
      this.addInfoRow(0, y, 'Destroyed By', wreck.destroyedByUserId.substring(0, 12) + '...', '#ff8888');
      y += 20;
    }

    y += 8;

    // Divider
    const divider = this.scene.add.graphics();
    divider.lineStyle(1, Colors.cyan, 0.1);
    divider.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(divider);
    y += 10;

    // Tow status
    const towColor = STATUS_COLORS[wreck.towStatus] || '#888888';
    const towStatusLabel = wreck.towStatus.replace(/_/g, ' ').toUpperCase();
    const towStatusText = this.scene.add.text(0, y, `Tow: ${towStatusLabel}`, {
      fontSize: FontSize.sm,
      color: towColor,
      fontFamily: Fonts.heading,
    });
    this.detailContainer.add(towStatusText);
    y += 20;

    // Show tow destination when towing
    if (wreck.towStatus === 'towing') {
      const destName = wreck.towDestination === 'earth' ? 'Earth (0, 0)' :
        wreck.towDestination === 'station' ? 'Nearest Station' : wreck.towDestination;
      const towDestText = this.scene.add.text(0, y, `Destination: ${destName}`, {
        fontSize: FontSize.xs,
        color: '#4488ff',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(towDestText);
      y += 16;

      const towInfoText = this.scene.add.text(0, y, 'Towing in progress — 1 hex per tick', {
        fontSize: FontSize.xs,
        color: '#8899bb',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(towInfoText);
      y += 16;
    } else if (wreck.towStatus === 'arrived') {
      const arrivedText = this.scene.add.text(0, y, 'Wreck has arrived — ready for repair!', {
        fontSize: FontSize.xs,
        color: '#44cc44',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(arrivedText);
      y += 16;
    }

    // Repair status
    const repairColor = REPAIR_STATUS_COLORS[wreck.repairStatus] || '#888888';
    const repairLabel = wreck.repairStatus.replace(/_/g, ' ').toUpperCase();
    const repairStatusText = this.scene.add.text(0, y, `Repair: ${repairLabel}`, {
      fontSize: FontSize.sm,
      color: repairColor,
      fontFamily: Fonts.heading,
    });
    this.detailContainer.add(repairStatusText);
    y += 24;

    // -- Wreck Claiming Info --
    const currentUserId = this.mmoState.getUserId();
    const isOwner = currentUserId === wreck.ownerId;
    const wreckAny = wreck as any;
    const claimedBy: string | null = wreckAny.claimed_by || wreckAny.claimedBy || null;
    const priorityExpiresAt: string | null = wreckAny.owner_priority_expires_at || wreckAny.ownerPriorityExpiresAt || null;
    const priorityExpired = !priorityExpiresAt || new Date(priorityExpiresAt).getTime() <= Date.now();

    // Only show claiming info if wreck is still awaiting decision and not owned by current player
    const isTowingOrBeyond = wreck.towStatus !== 'awaiting_decision';
    if (!isTowingOrBeyond && !isOwner) {
      if (priorityExpiresAt && !priorityExpired) {
        const remaining = formatTimeRemaining(priorityExpiresAt);
        const priorityText = this.scene.add.text(0, y, `OWNER PRIORITY: ${remaining.text} remaining`, {
          fontSize: FontSize.xs,
          color: '#ffcc44',
          fontFamily: Fonts.heading,
        });
        this.detailContainer.add(priorityText);
        y += 18;
      } else if (!claimedBy) {
        const publicText = this.scene.add.text(0, y, 'PUBLICLY CLAIMABLE', {
          fontSize: FontSize.xs,
          color: '#44cc44',
          fontFamily: Fonts.heading,
        });
        this.detailContainer.add(publicText);
        y += 18;
      }
    }

    if (claimedBy) {
      const claimLabel = claimedBy === currentUserId ? 'Claimed by: YOU' : `Claimed by: ${claimedBy.substring(0, 12)}...`;
      const claimText = this.scene.add.text(0, y, claimLabel, {
        fontSize: FontSize.xs,
        color: '#aaccdd',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(claimText);
      y += 18;
    }

    // Claim button: show if wreck is unclaimed and (priority expired OR user is owner during priority)
    if (!claimedBy && (priorityExpired || isOwner)) {
      const claimBtn = this.createActionButton(0, y, this.detailW, 32, 'CLAIM WRECK', 0x224466, () => this.handleClaim());
      this.detailContainer.add(claimBtn);
      y += 40;
    }

    // -- Actions based on status --

    // Awaiting decision: show tow options
    if (wreck.towStatus === 'awaiting_decision') {
      const towTitle = this.scene.add.text(0, y, 'TOW OPTIONS', {
        fontSize: FontSize.sm,
        color: CSSColors.textLight,
        fontFamily: Fonts.heading,
      });
      this.detailContainer.add(towTitle);
      y += 22;

      if (this.towOptions.length === 0) {
        const noOptions = this.scene.add.text(0, y, 'No tow options available.', {
          fontSize: FontSize.sm,
          color: '#667788',
          fontFamily: Fonts.body,
        });
        this.detailContainer.add(noOptions);
        y += 20;
      } else {
        for (const option of this.towOptions) {
          const optCard = this.scene.add.container(0, y);
          const optW = this.detailW;
          const optH = 60;

          const optBg = this.scene.add.graphics();
          optBg.fillStyle(0x161630, 0.6);
          optBg.fillRoundedRect(0, 0, optW, optH, Radius.sm);
          optBg.lineStyle(1, Colors.cyan, 0.08);
          optBg.strokeRoundedRect(0, 0, optW, optH, Radius.sm);
          optCard.add(optBg);

          const destText = this.scene.add.text(10, 8, option.destinationName, {
            fontSize: FontSize.sm,
            color: '#ffffff',
            fontFamily: Fonts.heading,
          });
          optCard.add(destText);

          const distText = this.scene.add.text(10, 28, `${option.distance} hex  \u00b7  ~${option.estimatedTicks} ticks`, {
            fontSize: FontSize.xs,
            color: '#889999',
            fontFamily: Fonts.body,
          });
          optCard.add(distText);

          const isdCost = Math.round(option.costIsd || 0);
          const costText = this.scene.add.text(10, 44, `${isdCost} $ISD`, {
            fontSize: FontSize.xs,
            color: CSSColors.gold,
            fontFamily: Fonts.body,
          });
          optCard.add(costText);
          const towBtn = this.createActionButton(optW - 72, 16, 60, 28, 'Tow', 0x224466, () =>
            this.confirmTow(option, isdCost),
          );
          optCard.add(towBtn);

          this.detailContainer.add(optCard);
          y += optH + 6;
        }
      }
    }

    // Towing: show progress
    if (wreck.towStatus === 'towing') {
      const towDest = wreck.towDestination === 'earth' ? 'Earth' : 'Station';
      const towingText = this.scene.add.text(0, y, `Towing to: ${towDest}`, {
        fontSize: FontSize.sm,
        color: '#4488ff',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(towingText);
      y += 20;

      if (wreck.towCostUsd > 0) {
        const costText = this.scene.add.text(0, y, `Tow cost: $${wreck.towCostUsd.toFixed(2)}`, {
          fontSize: FontSize.sm,
          color: CSSColors.gold,
          fontFamily: Fonts.body,
        });
        this.detailContainer.add(costText);
        y += 20;
      }

      const progressText = this.scene.add.text(0, y, 'In transit...', {
        fontSize: FontSize.sm,
        color: '#4488ff',
        fontFamily: Fonts.heading,
      });
      this.detailContainer.add(progressText);
      y += 24;
    }

    // Arrived: show repair costs
    if (wreck.towStatus === 'arrived' && wreck.repairStatus !== 'completed') {
      const repairTitle = this.scene.add.text(0, y, 'REPAIR COSTS', {
        fontSize: FontSize.sm,
        color: CSSColors.textLight,
        fontFamily: Fonts.heading,
      });
      this.detailContainer.add(repairTitle);
      y += 22;

      const mineralCosts = wreck.repairCostMinerals || {};
      const mineralEntries = Object.entries(mineralCosts);

      if (mineralEntries.length === 0) {
        const noCost = this.scene.add.text(0, y, 'No mineral cost (free repair)', {
          fontSize: FontSize.sm,
          color: '#44cc44',
          fontFamily: Fonts.body,
        });
        this.detailContainer.add(noCost);
        y += 20;
      } else {
        for (const [mineralName, amount] of mineralEntries) {
          const costLine = this.scene.add.text(0, y, `${mineralName}: ${amount}`, {
            fontSize: FontSize.sm,
            color: '#ffcc88',
            fontFamily: Fonts.body,
          });
          this.detailContainer.add(costLine);
          y += 18;
        }
      }

      y += 8;

      if (wreck.repairStatus === 'not_started') {
        const repairBtn = this.createActionButton(0, y, this.detailW, 32, 'Start Repair', 0x226622, () => this.handleRepair());
        this.detailContainer.add(repairBtn);
      } else if (wreck.repairStatus === 'repairing') {
        // Calculate repair progress
        const REPAIR_DURATION = 30; // seconds
        const startedAt = wreck.repairStartedAt ? new Date(wreck.repairStartedAt).getTime() : Date.now();
        const elapsedSec = Math.floor((Date.now() - startedAt) / 1000);
        const remaining = Math.max(0, REPAIR_DURATION - elapsedSec);
        const pct = Math.min(1, elapsedSec / REPAIR_DURATION);

        // Progress bar background
        const barW = this.detailW;
        const barH = 18;
        const barBg = this.scene.add.graphics();
        barBg.fillStyle(0x1a2a44, 1);
        barBg.fillRoundedRect(0, y, barW, barH, 3);
        this.detailContainer.add(barBg);

        // Progress bar fill
        const barFill = this.scene.add.graphics();
        barFill.fillStyle(0x4488ff, 1);
        if (pct > 0) {
          barFill.fillRoundedRect(0, y, Math.max(6, barW * pct), barH, 3);
        }
        this.detailContainer.add(barFill);

        // Progress text overlay
        const pctText = this.scene.add.text(barW / 2, y + barH / 2, `${Math.round(pct * 100)}%`, {
          fontSize: '11px', fontStyle: '900', color: '#ffffff', fontFamily: Fonts.heading,
        });
        pctText.setOrigin(0.5, 0.5);
        this.detailContainer.add(pctText);
        y += barH + 6;

        // Countdown text
        const countdownText = this.scene.add.text(0, y, remaining > 0 ? `${remaining}s remaining` : 'Completing...', {
          fontSize: FontSize.sm,
          color: '#4488ff',
          fontFamily: Fonts.heading,
        });
        this.detailContainer.add(countdownText);

        // Start polling to update the timer
        this.startRepairPolling();
      }
    }

    // Completed repair
    if (wreck.repairStatus === 'completed') {
      const completedText = this.scene.add.text(0, y, 'Repair complete!\nShip restored.', {
        fontSize: FontSize.sm,
        color: '#44cc44',
        fontFamily: Fonts.heading,
      });
      this.detailContainer.add(completedText);

      // Show completion popup once
      if (!this.repairCompletionShown) {
        this.repairCompletionShown = true;
        this.showRepairCompletePopup(wreck);
      }
    }

    // -- Salvage for Relics (non-owner, arrived, claimed by current user) --
    if (wreck.towStatus === 'arrived' && !isOwner && claimedBy === currentUserId) {
      // Divider
      y += 12;
      const salvageDivider = this.scene.add.graphics();
      salvageDivider.lineStyle(1, Colors.gold, 0.2);
      salvageDivider.lineBetween(0, y, this.detailW, y);
      this.detailContainer.add(salvageDivider);
      y += 10;

      const salvageHeading = this.scene.add.text(0, y, 'SALVAGE FOR RELICS', {
        fontSize: FontSize.sm,
        color: CSSColors.gold,
        fontFamily: Fonts.heading,
      });
      this.detailContainer.add(salvageHeading);
      y += 20;

      const salvageDesc = this.scene.add.text(0, y, 'Consume this wreck to search for technology\nrelics. You\'ll also receive bonus minerals.', {
        fontSize: FontSize.xs,
        color: '#aaccdd',
        fontFamily: Fonts.body,
        wordWrap: { width: this.detailW },
      });
      this.detailContainer.add(salvageDesc);
      y += 34;

      // Relic chance info
      const hasPropTech = wreckAny.has_proprietary_tech || wreckAny.hasProprietaryTech || false;
      const chanceLabel = hasPropTech ? 'Relic Chance: ~30%' : 'Relic Chance: ~15%';
      const chanceText = this.scene.add.text(0, y, chanceLabel, {
        fontSize: FontSize.xs,
        color: hasPropTech ? '#ffcc44' : '#aaccdd',
        fontFamily: Fonts.body,
      });
      this.detailContainer.add(chanceText);
      y += 22;

      // Salvage button (gold/amber colored)
      const salvageBtn = this.createActionButton(0, y, this.detailW, 32, 'SALVAGE', 0x665522, () => this.handleSalvage());
      this.detailContainer.add(salvageBtn);
      y += 40;
    }

    // Set max scroll based on content height vs visible area
    const detailVisibleH = this.dh - 58 - 30;
    this.detailMaxScrollY = Math.max(0, y - detailVisibleH);
  }

  // ============ Helpers ============

  /** Render a label: value info row */
  private addInfoRow(x: number, y: number, label: string, value: string, valueColor: string = '#aaccdd'): void {
    const labelText = this.scene.add.text(x, y, `${label}: `, {
      fontSize: FontSize.xs,
      color: '#889999',
      fontFamily: Fonts.body,
    });
    this.detailContainer.add(labelText);

    const valText = this.scene.add.text(x + labelText.width, y, value, {
      fontSize: FontSize.xs,
      color: valueColor,
      fontFamily: Fonts.body,
    });
    this.detailContainer.add(valText);
  }

  // ============ Actions ============

  private confirmTow(option: TowOption, isdCost: number): void {
    if (!this.selectedWreck) return;

    // Replace detail content with confirmation
    this.detailContainer.removeAll(true);
    let y = 10;

    const title = this.scene.add.text(this.detailW / 2, y, 'CONFIRM TOW', {
      fontSize: FontSize.md, color: CSSColors.gold, fontFamily: Fonts.heading,
    });
    title.setOrigin(0.5, 0);
    this.detailContainer.add(title);
    y += 30;

    const destLabel = this.scene.add.text(this.detailW / 2, y, `Tow to ${option.destinationName}`, {
      fontSize: FontSize.sm, color: '#ffffff', fontFamily: Fonts.body,
    });
    destLabel.setOrigin(0.5, 0);
    this.detailContainer.add(destLabel);
    y += 22;

    const distLabel = this.scene.add.text(this.detailW / 2, y, `${option.distance} hexes  ·  ~${option.estimatedTicks} ticks`, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    distLabel.setOrigin(0.5, 0);
    this.detailContainer.add(distLabel);
    y += 28;

    const costLabel = this.scene.add.text(this.detailW / 2, y, `Cost: ${isdCost.toLocaleString()} $ISD`, {
      fontSize: FontSize.md, color: CSSColors.gold, fontFamily: Fonts.heading,
    });
    costLabel.setOrigin(0.5, 0);
    this.detailContainer.add(costLabel);
    y += 40;

    // Confirm button
    const confirmBtn = this.createActionButton(
      (this.detailW - 140) / 2, y, 140, 34, 'CONFIRM TOW', 0x226644,
      () => this.executeTow(option),
    );
    this.detailContainer.add(confirmBtn);
    y += 44;

    // Buy ISD button
    const buyBtn = this.createActionButton(
      (this.detailW - 140) / 2, y, 140, 34, 'BUY $ISD', 0x664422,
      () => EventBus.emit('open_buy_isd_dialog'),
    );
    this.detailContainer.add(buyBtn);
    y += 44;

    // Cancel button
    const cancelBtn = this.createActionButton(
      (this.detailW - 140) / 2, y, 140, 34, 'CANCEL', 0x333344,
      () => this.renderDetail(),
    );
    this.detailContainer.add(cancelBtn);
  }

  private async executeTow(option: TowOption): Promise<void> {
    if (!this.selectedWreck) return;

    // Deactivate confirm button and show "Towing..."
    this.detailContainer.removeAll(true);
    const towingText = this.scene.add.text(this.detailW / 2, 40, 'TOWING...', {
      fontSize: FontSize.md, color: CSSColors.gold, fontFamily: Fonts.heading,
    });
    towingText.setOrigin(0.5, 0);
    this.detailContainer.add(towingText);

    const result = await this.monetization.requestTow(
      this.selectedWreck.id,
      option.destination,
      option.destinationId,
    );

    if (result.success) {
      const isdCost = Math.round(option.costIsd || option.costUsd || 0);
      this.statusText.setText(`Towing to ${option.destinationName}! (${isdCost.toLocaleString()} $ISD deducted)`);
      // Refresh balance display in HUD
      EventBus.emit('send_message', { type: 'mmo_get_resources', payload: { worldId: 1 } });
      this.loadData();
    } else if (result.error?.includes('Insufficient $ISD')) {
      this.statusText.setText(result.error);
      EventBus.emit('open_buy_isd_dialog');
      this.renderDetail();
    } else {
      this.statusText.setText(result.error || 'Tow failed');
      this.renderDetail();
    }
  }

  private async handleRepair(): Promise<void> {
    if (!this.selectedWreck) return;

    this.statusText.setText('Starting repair...');
    const result = await this.monetization.startRepair(this.selectedWreck.id);

    if (result.success) {
      this.statusText.setText('Repair started!');
      this.loadData();
    } else if (result.error && result.error.includes('Insufficient')) {
      // Missing minerals — get a quote for auto-buying from auction house
      this.statusText.setText('Missing minerals. Checking auction house...');
      await this.showRepairQuote();
    } else {
      this.statusText.setText(result.error || 'Repair failed');
    }
  }

  private async showRepairQuote(): Promise<void> {
    if (!this.selectedWreck || !this.statusText || !this.scene) return;
    const API_URL = (await import('../../utils/ApiConfig.js')).getApiBaseUrl();
    const token = localStorage.getItem('crimsonmandate_token');

    try {
      const res = await fetch(`${API_URL}/wrecks/repair-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ wreckId: this.selectedWreck.id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        this.statusText.setText(data.error || 'Failed to get repair quote');
        return;
      }

      // Build the quote display
      const missing = data.breakdown.filter((b: any) => b.missing > 0);
      if (missing.length === 0) {
        // Player has all minerals now (maybe acquired between check and quote)
        this.statusText.setText('You have all minerals. Try repairing again.');
        return;
      }

      const mineralNames: Record<string, string> = {
        min_iron: 'Iron', min_copper: 'Copper', min_titanium: 'Titanium',
        min_platinum: 'Platinum', min_rhodium: 'Rhodium', min_palladium: 'Palladium',
        min_iridium: 'Iridium', min_darkmat: 'Dark Matter',
      };

      let msg = 'Missing minerals:\n';
      for (const m of missing) {
        const name = mineralNames[m.mineral] || m.mineral;
        msg += `  ${name}: need ${m.missing} (${m.auctionAvailable > 0 ? `${(m.totalCost || 0).toFixed(1)} ISD` : 'NOT ON SALE'})\n`;
      }

      if (!data.allMineralsAvailable) {
        this.statusText.setText(msg + '\nSome minerals are not listed on the auction house.');
        return;
      }

      msg += `\nTotal cost: ${(data.grandTotal || 0).toFixed(2)} ISD (incl. 2.5% fee)`;
      msg += `\nYour balance: ${(data.playerIsdBalance || 0).toFixed(2)} ISD`;

      if (!data.canAfford) {
        msg += '\n\nInsufficient ISD! Buy more from the Tokenomics page.';
        this.statusText.setText(msg);
        return;
      }

      // Show centered popup overlay with quote and Buy/Cancel buttons
      this.statusText.setText('');
      const cam = this.scene.cameras.main;
      const popupW = 340;
      const popupH = Math.min(280, 120 + missing.length * 22);
      const px = cam.width / 2 - popupW / 2;
      const py = cam.height / 2 - popupH / 2;

      // Dimmed background
      const overlay = this.scene.add.rectangle(cam.width / 2, cam.height / 2, cam.width, cam.height, 0x000000, 0.6)
        .setDepth(9998).setInteractive();
      // Popup panel
      const panel = this.scene.add.rectangle(px + popupW / 2, py + popupH / 2, popupW, popupH, 0x0d1b2a, 1)
        .setStrokeStyle(2, 0x4a9eff).setDepth(9999);

      let textY = py + 16;
      const title = this.scene.add.text(px + popupW / 2, textY, 'Buy Minerals & Repair', {
        fontSize: '16px', fontStyle: 'bold', color: '#FFD700', fontFamily: 'monospace',
      }).setOrigin(0.5, 0).setDepth(10000);
      textY += 28;

      const lines: Phaser.GameObjects.Text[] = [title];
      for (const m of missing) {
        const name = mineralNames[m.mineral] || m.mineral;
        const costStr = m.auctionAvailable > 0 ? `${(m.totalCost || 0).toFixed(1)} ISD` : 'NOT ON SALE';
        const line = this.scene.add.text(px + 20, textY, `${name}: ${m.missing} needed (${costStr})`, {
          fontSize: '12px', color: '#c8d6e5', fontFamily: 'monospace',
        }).setDepth(10000);
        lines.push(line);
        textY += 20;
      }

      textY += 8;
      const totalLine = this.scene.add.text(px + 20, textY, `Total: ${(data.grandTotal || 0).toFixed(2)} ISD (incl. 2.5% fee)`, {
        fontSize: '13px', fontStyle: 'bold', color: '#ff9800', fontFamily: 'monospace',
      }).setDepth(10000);
      lines.push(totalLine);
      textY += 18;
      const balLine = this.scene.add.text(px + 20, textY, `Your balance: ${(data.playerIsdBalance || 0).toFixed(2)} ISD`, {
        fontSize: '12px', color: data.canAfford ? '#44ff66' : '#ff4444', fontFamily: 'monospace',
      }).setDepth(10000);
      lines.push(balLine);
      textY += 28;

      const destroyPopup = () => {
        overlay.destroy(); panel.destroy();
        lines.forEach(l => l.destroy());
        buyBtn.destroy(); cancelBtn.destroy();
      };

      // Buy button
      const buyBtn = this.scene.add.text(px + popupW / 2 - 80, textY, data.canAfford ? '  Buy & Repair  ' : '  Not Enough ISD  ', {
        fontSize: '14px', fontStyle: 'bold', color: data.canAfford ? '#00ff88' : '#666',
        fontFamily: 'monospace', backgroundColor: data.canAfford ? '#1a4422' : '#1a1a2e',
        padding: { x: 8, y: 6 },
      }).setDepth(10000).setInteractive({ useHandCursor: data.canAfford });

      if (data.canAfford) {
        buyBtn.on('pointerdown', async () => {
          buyBtn.setText('  Purchasing...  ');
          buyBtn.disableInteractive();
          try {
            const buyRes = await fetch(`${API_URL}/wrecks/repair-with-purchase`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
              body: JSON.stringify({ wreckId: this.selectedWreck!.id }),
            });
            const buyData = await buyRes.json();
            destroyPopup();
            if (!buyRes.ok || !buyData.success) {
              this.statusText.setText(buyData.error || 'Purchase failed');
              return;
            }
            this.statusText.setText(`Repair started! Spent ${(buyData.totalIsdSpent || 0).toFixed(2)} ISD on minerals.`);
            this.loadData();
          } catch (err) {
            destroyPopup();
            this.statusText.setText('Purchase failed: ' + (err instanceof Error ? err.message : 'Unknown error'));
          }
        });
      }

      // Cancel button
      const cancelBtn = this.scene.add.text(px + popupW / 2 + 40, textY, '  Cancel  ', {
        fontSize: '14px', color: '#aaaaaa', fontFamily: 'monospace',
        backgroundColor: '#2a2a3e', padding: { x: 8, y: 6 },
      }).setDepth(10000).setInteractive({ useHandCursor: true });
      cancelBtn.on('pointerdown', () => { destroyPopup(); });
    } catch (err) {
      this.statusText.setText('Failed to get quote: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  }

  private async handleSalvage(): Promise<void> {
    if (!this.selectedWreck) return;

    this.statusText.setText('Salvaging wreck...');
    const result = await this.monetization.salvageWreck(this.selectedWreck.id);

    if (result.success) {
      const parts: string[] = [];
      if (result.relic) {
        parts.push(`Relic found: ${result.relic.name} (Tier ${result.relic.tier})`);
      }
      if (result.minerals && Object.keys(result.minerals).length > 0) {
        const mineralStr = Object.entries(result.minerals)
          .map(([name, qty]) => `${name}: ${qty}`)
          .join(', ');
        parts.push(`Minerals: ${mineralStr}`);
      }
      this.statusText.setText(parts.length > 0 ? parts.join(' | ') : 'Salvage complete!');
      this.loadData();
    } else {
      this.statusText.setText(result.error || 'Salvage failed');
    }
  }

  private async handleClaim(): Promise<void> {
    if (!this.selectedWreck) return;

    this.statusText.setText('Claiming wreck...');
    const result = await this.monetization.claimWreck(this.selectedWreck.id);

    if (result.success) {
      this.statusText.setText('Wreck claimed!');
      this.loadData();
    } else {
      this.statusText.setText(result.error || 'Claim failed');
    }
  }

  private createActionButton(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    color: number,
    callback: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(color, 0.8);
    bg.fillRoundedRect(0, 0, w, h, Radius.sm);
    bg.lineStyle(1, Colors.cyan, 0.2);
    bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.sm,
      color: '#ffffff',
      fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    btn.add(text);

    const hitZone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(color + 0x222222, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.3);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(color, 0.8);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, 0.2);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', callback);
    btn.add(hitZone);

    return btn;
  }
}
